<script setup>
import { ref, computed } from "vue";
import { Handle, Position, useVueFlow } from "@vue-flow/core";
import { NodeToolbar } from "@vue-flow/node-toolbar";
import { removeNodeOccurrences } from "@/mixins/common-utils.js";

const props = defineProps(["data", "nodeInfo"]);
const mainColor = computed(() =>
    props.nodeInfo.type == "inputNode" ? props.nodeInfo.data.color : "red",
);
const isInputNode = computed(
    () => props.nodeInfo.data.subCategory == "inputEntry",
);
const hasErrors = computed(() => props.nodeInfo.data?.errors?.length > 0);
const hasWarnings = computed(() => props.nodeInfo.data?.warnings?.length > 0);

const computedDebugSize = computed(() => {
    let size = 50;
    // Increase size if both errors and warnings are present
    if (hasErrors.value && hasWarnings.value) {
        size += 30; // Adjust the value as needed
    }
    return size;
});

const isInputFileNode = computed(
    () => props.nodeInfo.data.subCategory == "fileEntry",
);
const isOutputNode = computed(() => props.nodeInfo.type == "outputNode");

const { nodes, removeNodes, findNode } = useVueFlow();
const node = computed(() => findNode(props.nodeInfo.id));

// remove a single node from the graph
function onRemoveNode(id) {
    removeNodeOccurrences(id, nodes);
    removeNodes(id);
}

const emit = defineEmits([
    "toggleDialogOperator",
    "preview",
    "toggleDialogDebug",
]);

function editNode() {
    emit("toggleDialogOperator", true, props.nodeInfo.id);
    // manageNodePosition();
}
//Call node Preview
function onPreview() {
    emit("preview", true, props.nodeInfo.label, props.nodeInfo.id);
}
//Call mashup Debug
function onDebug() {
    emit(
        "toggleDialogDebug",
        true,
        props.nodeInfo.id,
        props.nodeInfo.label,
        props.nodeInfo.data,
    );
}
</script>

<template>
    <div>
        <NodeToolbar
            :is-visible="nodeInfo.data?.toolbarVisible"
            :position="nodeInfo.data?.toolbarPosition"
            align="start"
            offset="8"
            class="mx-1"
        >
            <v-tooltip :text="$t('labels.setting')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-cog"
                        color="grey-darken-4"
                        size="small"
                        @click="editNode()"
                    ></v-btn
                ></template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.preview')" location="top">
                <template v-slot:activator="{ props }" v-if="isInputFileNode">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-play"
                        color="green-lighten-1"
                        class="ml-1"
                        size="small"
                        @click="onPreview()"
                    ></v-btn>
                </template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.delete')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-delete"
                        color="red-lighten-1"
                        class="mx-1"
                        size="small"
                        @click="onRemoveNode(nodeInfo.id)"
                    ></v-btn>
                </template>
            </v-tooltip>
        </NodeToolbar>

        <v-tooltip
            :text="$t('strings.double_click_to_setup')"
            location="bottom"
            :open-on-hover="!isOutputNode"
        >
            <template v-slot:activator="{ props }">
                <v-card
                    v-bind="props"
                    class="mx-auto"
                    max-width="250"
                    :color="mainColor"
                    variant="elevated"
                    @dblclick="editNode()"
                >
                    <v-card-item :class="!isOutputNode ? 'py-1 px-2' : 'pa-2'">
                        <template v-slot:prepend>
                            <v-avatar
                                size="small"
                                :color="`${mainColor}-lighten-3`"
                            >
                                <v-icon
                                    :icon="nodeInfo.data.icon"
                                    :color="`${mainColor}-darken-4`"
                                ></v-icon>
                            </v-avatar>
                        </template>
                        <template v-slot:title>
                            <div class="text-overline font-weight-medium">
                                {{
                                    node.userData &&
                                    nodeInfo.type == "inputNode"
                                        ? node.userData.label
                                        : nodeInfo.id
                                }}
                            </div>
                        </template>
                        <template v-slot:subtitle>
                            <div
                                v-if="!isOutputNode"
                                class="text-caption font-weight-light text-capitalize text-left"
                            >
                                {{ `${nodeInfo.id}` }}
                            </div>
                        </template>

                        <!-- DEBUG BADGES -->
                        <template v-slot:append>
                            <v-sheet
                                v-if="hasErrors || hasWarnings"
                                :color="`${nodeInfo.data.color}-lighten-3`"
                                rounded="sm"
                                :width="computedDebugSize"
                                height="50"
                                class="d-inline-flex align-center"
                            >
                                <!-- Error Badge -->
                                <v-badge
                                    v-if="hasErrors"
                                    color="error"
                                    :content="nodeInfo.data?.errors?.length"
                                    :bordered="true"
                                    class="mx-auto"
                                    offset-y="-2"
                                    offset-x="1"
                                >
                                    <v-btn
                                        icon="mdi-alert-octagon"
                                        density="compact"
                                        color="error"
                                        class="no-text"
                                        @click="onDebug"
                                    >
                                        <v-icon
                                            icon="mdi-alert-octagon"
                                            size="small"
                                        ></v-icon>
                                        <v-tooltip
                                            activator="parent"
                                            location="top"
                                        >
                                            {{ $t("strings.operator-error") }}
                                        </v-tooltip>
                                    </v-btn>
                                </v-badge>
                                <!-- Warning Badge -->
                                <v-badge
                                    v-if="hasWarnings"
                                    color="warning"
                                    :content="nodeInfo.data?.warnings?.length"
                                    :bordered="true"
                                    class="mx-auto"
                                    offset-y="-2"
                                    offset-x="3"
                                >
                                    <v-btn
                                        icon="mdi-alert"
                                        density="compact"
                                        color="warning"
                                        class="no-text"
                                        @click="onDebug"
                                    >
                                        <v-icon
                                            icon="mdi-alert"
                                            size="small"
                                        ></v-icon>
                                        <v-tooltip
                                            activator="parent"
                                            location="top"
                                        >
                                            {{ $t("strings.operator-warning") }}
                                        </v-tooltip>
                                    </v-btn>
                                </v-badge>
                            </v-sheet>
                        </template>
                    </v-card-item>
                </v-card>
            </template>
        </v-tooltip>

        <Handle
            v-if="nodeInfo.type == 'outputNode' ? true : false"
            type="target"
            :position="Position.Left"
            class="handleStyle handleStyleTarget"
            :id="`left-handle-${nodeInfo.id}`"
        />
        <Handle
            v-if="nodeInfo.type == 'inputNode' ? true : false"
            type="source"
            :position="Position.Right"
            class="handleStyle handleStyleSource"
            :id="`right-handle-${nodeInfo.id}`"
        />
    </div>
</template>

<style scoped>
.handleStyle {
    background: #999;
    height: 12px;
    width: 10px;
    border-radius: 3px;
    border: 0.5px solid #777;
}
</style>
